import * as React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Moon, Sun } from 'lucide-react';
import { BrandLogo, useMeBranding } from '@/lib/branding';
import { useAuth, adminPortal } from '@/store/auth';
import { usePhone } from '@/store/phone';
import { useAgentMe, useAgentState, freshRegistration } from '@/hooks/agent';
import { api, errorText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/badge';
import { Softphone } from '@/components/Softphone';
import { LiveIndicator } from '@/components/LiveIndicator';
import { LanguageMenu } from '@/components/LanguageMenu';
import { InstallButton } from '@/lib/pwa';
import { useTheme } from '@/components/layout/AdminLayout';
import { useLiveRefresh, useRealtimeConnection } from '@/hooks/live';
import { toast } from '@/store/toast';
import { cn, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';

const TABS = [['/agent/dashboard', 'Dashboard'], ['/agent/call', 'Call'], ['/agent/history', 'History'], ['/agent/callbacks', 'Callbacks'], ['/agent/profile', 'Profile']];
// Both mode (docs/PBX.md): an agent whose phone is also a PBX extension gets its page too
const PHONE_TAB = ['/agent/my-phone', 'My phone'];
const PAUSE_REASONS = ['BREAK', 'LUNCH', 'MEETING', 'TRAINING', 'TECH'];

export function AgentLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const theme = useTheme();
  useMeBranding();
  const me = useAgentMe();
  const auth = useQuery({ queryKey: ['me', user?.id, 'agent'], staleTime: 60_000,
                          queryFn: () => api<{ tenant: { modules?: string[] } | null; extension: { id: number; number: string } | null }>('/auth/me') });
  const myExt = !!auth.data?.extension && !!auth.data?.tenant?.modules?.includes('PBX');
  const st = useAgentState();
  const phone = usePhone();
  const [reason, setReason] = React.useState('BREAK');
  const state = st.data?.agent.state ?? me.data?.agent.state ?? '...';
  const lastState = React.useRef<string | null>(null);
  useRealtimeConnection();

  useLiveRefresh(['agent.state', 'disposition.required', 'call.ringing', 'call.answered', 'call.bridged', 'call.hangup'], () => {
    qc.invalidateQueries({ queryKey: ['agent-state'] });
    qc.invalidateQueries({ queryKey: ['agent-call'] });
  });

  // connect the softphone once the SIP settings are known; a logged-out agent (a new portal sign-in)
  // starts from a clean registration: the engine drops every old one first
  React.useEffect(() => {
    if (me.data && phone.reg === 'disconnected') {
      (me.data.agent.state === 'LOGGED_OUT' ? freshRegistration(me.data.sip) : phone.connect(me.data.sip)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data]);

  // screen pop: a CRM match for a ringing call opens the call page, where the CRM panel shows it
  useLiveRefresh(['crm.screenpop'], (e) => {
    if (e.data.status === 'MATCHED' || e.data.status === 'MULTIPLE') { if (loc.pathname !== '/agent/call') nav('/agent/call'); }
  });

  // a finished call needs a disposition: bring the agent to the call page
  React.useEffect(() => {
    if (state === 'WRAPUP' && lastState.current !== 'WRAPUP' && loc.pathname !== '/agent/call') nav('/agent/call');
    lastState.current = state;
  }, [state, loc.pathname, nav]);

  async function act(path: string, body?: unknown) {
    try { await api(`/agent/${path}`, { body: body ?? {} }); await qc.invalidateQueries({ queryKey: ['agent-state'] }); }
    catch (e) { toast.error(errorText(e)); }
  }

  // login and logout start from a clean SIP registration: every old one is dropped, one new REGISTER
  const [regBusy, setRegBusy] = React.useState(false);
  async function login() {
    setRegBusy(true);
    try {
      if (!await freshRegistration(me.data?.sip)) toast.error(t('Softphone did not register again; logging in anyway'));
      await act('login', { initial: 'PAUSED' });
    } finally { setRegBusy(false); }
  }
  async function logoutAgent() {
    setRegBusy(true);
    try { await act('logout'); await freshRegistration(me.data?.sip); await qc.invalidateQueries({ queryKey: ['agent-state'] }); }
    finally { setRegBusy(false); }
  }

  async function signOut() {
    if (state !== 'LOGGED_OUT' && state !== '...') await api('/agent/logout', { body: {} }).catch(() => {});
    await freshRegistration(me.data?.sip, false).catch(() => {});      // drop every registration, no new one
    await phone.disconnect();
    logout();
    nav('/login');
  }

  const registered = phone.reg === 'registered';
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b bg-background/95 px-4 py-2 backdrop-blur">
        <div className="flex items-center gap-2 font-semibold"><BrandLogo className="h-5 w-5" /> {me.data?.agent.name ?? user?.name} <span className="text-sm font-normal text-muted-foreground">#{me.data?.agent.code}</span></div>
        <span data-testid="agent-state"><StatusBadge value={state} /></span>
        {st.data?.agent.reason && <span className="text-xs text-muted-foreground">{enumLabel(st.data.agent.reason)}</span>}
        <div className="flex flex-wrap items-center gap-2">
          {state === 'LOGGED_OUT' && <Button size="sm" disabled={regBusy} onClick={login}>{t('Log in')}</Button>}
          {(state === 'PAUSED' || state === 'DISCONNECTED') && (
            <Button size="sm" variant="success" disabled={!registered} title={registered ? '' : t('Your softphone must be registered first')} onClick={() => act('ready')}>{t('Ready')}</Button>
          )}
          {state === 'READY' && (<>
            <Select className="h-8 w-32" value={reason} onChange={(e) => setReason(e.target.value)} aria-label={t('Pause reason')}>{PAUSE_REASONS.map((r) => <option key={r} value={r}>{enumLabel(r)}</option>)}</Select>
            <Button size="sm" variant="warning" onClick={() => act('pause', { reason })}>{t('Pause')}</Button>
          </>)}
          {(state === 'PAUSED' || state === 'READY' || state === 'DISCONNECTED') && <Button size="sm" variant="outline" disabled={regBusy} onClick={logoutAgent}>{t('Log out')}</Button>}
          {!registered && state !== 'LOGGED_OUT' && <span className="text-xs text-destructive">{t('Softphone not registered: you cannot go Ready')}</span>}
        </div>
        <nav className="-mx-1 flex max-w-full items-center gap-1 overflow-x-auto px-1 sm:ml-auto" aria-label={t('Agent')}>
          {[...TABS, ...(myExt ? [PHONE_TAB] : [])].map(([to, label]) => (
            <NavLink key={to} to={to} className={({ isActive }) => cn('shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm hover:bg-accent', isActive && 'bg-accent font-medium')}>{t(label)}</NavLink>
          ))}
          {adminPortal(user) && <NavLink to="/admin" className="rounded-md px-3 py-1.5 text-sm hover:bg-accent" data-testid="to-admin">{t('Admin portal')}</NavLink>}
          <LiveIndicator />
          <InstallButton compact />
          <LanguageMenu />
          <Button variant="ghost" size="icon" aria-label={t('Toggle theme')} onClick={theme.toggle}>{theme.dark ? <Sun /> : <Moon />}</Button>
          <Button variant="outline" size="sm" onClick={signOut}><LogOut /> {t('Sign out')}</Button>
        </nav>
      </header>
      <div className="grid gap-4 p-4 lg:grid-cols-[1fr_320px]">
        <main className="min-w-0"><Outlet /></main>
        <aside className="lg:sticky lg:top-16 lg:self-start"><Softphone /></aside>
      </div>
    </div>
  );
}
